// D:\brickbucket_v3.1\Brickbucket_Frontend_V3.1\src\ConsumerApp\Consumer_app.js

import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// ==========================================
// 1. ACTIVE MODERN DESIGN SYSTEM SCREENS
// ==========================================
import HomeScreen from './Screens/Home/Home';
import SearchScreen from './Screens/Search/SearchScreen';
import ProductsList from './Screens/ProductsList/ProductsList';
import ProductDetailsScreen from './Screens/ProductDetails/ProductDetailsScreen';
import BucketScreen from './Screens/Bucket/Bucket';
import ProfileContent from './Screens/Account/ProfileContent';
import SupportView from './Screens/Account/views/SupportView';
import OffersView from './Screens/Account/views/OffersView';
import WalletView from './Screens/Account/views/WalletView';
import PoliciesView from './Screens/Account/views/PoliciesView';
import InvoicesView from './Screens/Account/views/InvoicesView';
import SavedItemsView from './Screens/Account/views/SavedItemsView';
import RfqsView from './Screens/Account/views/RfqsView';

// Nested Account Architecture
import AccountLayout from './Screens/Account/AccountLayout';
import OrdersMasterDetailView from './Screens/Account/views/OrdersMasterDetailView';
/* import DeliveryLocationsView from './Screens/Account/views/DeliveryLocationsView';
import WalletView from './Screens/Account/views/WalletView';
import RfqsView from './Screens/Account/views/RfqsView';
import InvoicesView from './Screens/Account/views/InvoicesView';
import SavedItemsView from './Screens/Account/views/SavedItemsView';
import SupportView from './Screens/Account/views/SupportView'; */

// ==========================================
// 2. AUTHENTICATION & ONBOARDING (LEGACY PRESERVED)
// ==========================================
import ConsumerLogin from './ConsumerLogin';
import OTPpage from './OTPpage';
import AddUserDetails from './AddUserDetails';
import NotApproved from './NotApproved';
import Approved from './Approved';
import RoleSelection from './RoleSelection';

// ==========================================
// 3. PROJECT & ORDER MANAGEMENT (LEGACY PRESERVED)
// ==========================================
import AddProject from './AddProject';
import ProjectDetails from './ProjectDetails';
import ViewProject from './ViewProject';
import Projects from './Projects';
import ProfileAccount from './ProfileAccount';
import UserDashboard from './UserDashboard';
import Orders from './Orders';
import OrderDetails from './OrderDetails';
import CustomerOrderDetails from './Orders/order_details';
import TrackItems from './TrackItems';

// ==========================================
// 4. E-COMMERCE & CHECKOUT FLOW (LEGACY PRESERVED)
// ==========================================
import ProductCategories from './ProductCategories';
import ProductDetails from './ProductDetails';
import ProductInformation from './ProductInformation';
import Vendoritems from './Vendoritems';
import CartPreview from './CartPreview';
import SavedItems from './SavedItems';
import AddAddress from './AddAddress';
import PaymentMethod from './PaymentMethod';
import Upipayments from './UpiPayments';
import Buildcoins from './Buildcoins';
import BuildcoinsTrn from './BuildcoinsTransactions';

// ==========================================
// 5. RFQ & ESTIMATION MODULES (LEGACY PRESERVED)
// ==========================================
import RFQ from './RFQ';
import RFQList from './RFQS/RFQ_List';
import RFQDetails from './RFQS/RFQ_details';
import ProjectQuote from './ProjectQuote';
import ProjectQuoteModal from './ProjectQuoteModal';
import ListPage from './ListPage';
import UMR from './UMR';
import MaterialList from './Journey/MaterialList';
import ReviewItemList from './Journey/ReviewItemList';
import ProjectQuotes from './Journey/ProjectQuotes';

// ==========================================
// 6. NESTED / LEGACY QUOTATION FLOWS (LEGACY PRESERVED)
// ==========================================
import Main_Page from './mainPage';
import Home from './Home';
import ViewTilesQuote from './ViewTilesQuote';
import ItemsforQuote from './ItemsforSO';
import DoorsQuote from './DoorsQuote';
import ViewQuotationDetails from './ViewQuotationDetails';
import WindowSummary from './WindowSummary';
import QuotationSummary from './QuotationSummary';
import DoorSummary from './DoorSummary';
import DoorQuotationSummary from './DoorQuotationSummary';
import TilesOrderSummary from './TilesOrderSummary';
import MaterialRequirement from './MaterialRequirement';
import MaterialReq from './MaterialReq';
import MaterialReq2 from './MaterialReq2';
import QuotationDetails from './QuotationDetails';
import WindowsQuotationDetails from './WindowsQuotationDetails';
import WindowsEconomyQuote from './WindowQuote';
import DoorQuotationDetails from './DoorQuotationDetails';
import TilesQuote from './TilesQuote';
import WindowQuote from './WindowQuote';
import QuoteSummary from './TilesQuoteSummary';
import DoorsQuoteSummary from './DoorsQuoteSummary';
import WindowsQuotationSummary from './WindowsQuoteSummary';
import SummaryPage from './SummaryPage';
import TilesStandardQuoteDetails from './Tiles/TilesStandardQuoteDetails';
import TilesCustomize from './Tiles/TilesCustomize';
import WindowsStandardQuoteDetails from './Windows/WindowsStandardQuoteDetails';
import WindowsCustomize from './Windows/WindowsCustomize';
import DoorsStandardQuoteDetails from './Doors/DoorsStandardQuoteDetails';
import DoorsCustomize from './Doors/DoorsCustomize';
import Proceed from './Proceed';

// ==========================================
// 🔒 SCOPED REACT QUERY CLIENT (DEFINED AFTER ALL IMPORTS)
// ==========================================
const consumerQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 2,
    },
  },
});

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const publicPaths = [
      '/consumer',
      '/consumer/otp',
      '/consumer/UserDetails',
      '/consumer/NotApproved',
      '/consumer/approved',
    ];

    const isPublic = publicPaths.includes(location.pathname);
    const sessionUser = sessionStorage.getItem('userData');
    const localUser = localStorage.getItem('userData');

    // Protected Route Check
    if (!isPublic && location.pathname.startsWith('/consumer')) {
      if (!sessionUser && !localUser) {
        navigate('/consumer', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  return (
    <QueryClientProvider client={consumerQueryClient}>
      <Routes>
        {/* ============================================================= */}
        {/* ⭐ PRIMARY ACTIVE CONSUMER ROUTES (NEW DESIGN SYSTEM)         */}
        {/* ============================================================= */}
        <Route path="/consumer/HomeScreen" element={<HomeScreen />} />
        <Route path="/consumer/productslist" element={<ProductsList />} />
        <Route path="/consumer/bucket" element={<BucketScreen />} />
        <Route path="/consumer/productdetails" element={<ProductDetailsScreen />} />
        <Route path="/consumer/search" element={<SearchScreen />} />

        {/* ------------------------------------------------------------- */}
        {/* 🏢 NESTED ACCOUNT ROUTING (RESPONSIVE MASTER-DETAIL LAYOUT)   */}
        {/* ------------------------------------------------------------- */}
        <Route path="/consumer/account" element={<AccountLayout />}>
          <Route
            index
            element={
              typeof window !== 'undefined' && window.innerWidth >= 768 ? (
                <Navigate to="orders" replace />
              ) : null
            }
          />
          <Route path="orders" element={<OrdersMasterDetailView />} />
          <Route path="orders/:orderId" element={<OrdersMasterDetailView />} />
          <Route path="profile" element={<ProfileContent />} />
          <Route path="support" element={<SupportView />} />
          <Route path="offers" element={<OffersView />} />
          <Route path="wallet" element={<WalletView />} />
          <Route path="policies" element={<PoliciesView />} />
          <Route path="invoices" element={<InvoicesView />} />
          <Route path="saved-items" element={<SavedItemsView />} />
          <Route path="rfqs" element={<RfqsView />} />

   {/*        <Route path="delivery" element={<DeliveryLocationsView />} />
          <Route path="wallet" element={<WalletView />} />
          <Route path="rfqs" element={<RfqsView />} />
          <Route path="invoices" element={<InvoicesView />} />
          <Route path="saved" element={<SavedItemsView />} />
          <Route path="support" element={<SupportView />} /> */}
        </Route>

        {/* Backward Compatibility Route for AccountScreen */}
        <Route
          path="/consumer/AccountScreen"
          element={
            <Navigate
              to={
                typeof window !== 'undefined' && window.innerWidth >= 768
                  ? '/consumer/account/orders'
                  : '/consumer/account'
              }
              replace
            />
          }
        />

        {/* ============================================================= */}
        {/* 🔐 AUTHENTICATION & ONBOARDING ROUTES                         */}
        {/* ============================================================= */}
        <Route path="/consumer" element={<ConsumerLogin />} />
        <Route path="/consumer/otp" element={<OTPpage />} />
        <Route path="/consumer/UserDetails" element={<AddUserDetails />} />
        <Route path="/consumer/NotApproved" element={<NotApproved />} />
        <Route path="/consumer/approved" element={<Approved />} />
        <Route path="/consumer/roleselection" element={<RoleSelection />} />

        {/* ============================================================= */}
        {/* 📦 CONSUMER APP CORE FEATURES (LEGACY PRESERVED)              */}
        {/* ============================================================= */}
        <Route path="/consumer/addproject" element={<AddProject />} />
        <Route path="/consumer/projectdetails" element={<ProjectDetails />} />
        <Route path="/consumer/viewproject" element={<ViewProject />} />
        <Route path="/consumer/projects" element={<Projects />} />
        <Route path="/consumer/profileaccount" element={<ProfileAccount />} />
        <Route path="/consumer/userdashboard" element={<UserDashboard />} />
        <Route path="/consumer/orders" element={<Orders />} />
        <Route path="/consumer/productdetails" element={<ProductDetails />} />
        <Route path="/consumer/cartpreview" element={<CartPreview />} />
        <Route path="/consumer/buildcoins" element={<Buildcoins />} />
        <Route path="/consumer/bctrn" element={<BuildcoinsTrn />} />
        <Route path="/consumer/productinformation" element={<ProductInformation />} />
        <Route path="/consumer/addaddress" element={<AddAddress />} />
        <Route path="/consumer/PaymentMethod" element={<PaymentMethod />} />
        <Route path="/consumer/upipayments" element={<Upipayments />} />
        <Route path="/consumer/productcategories" element={<ProductCategories />} />
        <Route path="/consumer/vendoritems" element={<Vendoritems />} />
        <Route path="/consumer/rfq" element={<RFQ />} />
        <Route path="/consumer/trackitems" element={<TrackItems />} />
        <Route path="/consumer/projectquote" element={<ProjectQuote />} />
        <Route path="/consumer/umr" element={<UMR />} />
        <Route path="/consumer/projectquotemodal" element={<ProjectQuoteModal />} />
        <Route path="/consumer/listpage" element={<ListPage />} />
        <Route path="/consumer/saveditems" element={<SavedItems />} />

        {/* ============================================================= */}
        {/* 📑 LEGACY NESTED WORKFLOWS                                    */}
        {/* ============================================================= */}
        <Route path="/consumer/app" element={<Main_Page />}>
          <Route path="home" element={<Home />} />
          <Route path="materialrequirement" element={<MaterialRequirement />} />
          <Route path="materialreq" element={<MaterialReq />} />
          <Route path="materialreq2" element={<MaterialReq2 />} />
          <Route path="tilesquote" element={<TilesQuote />} />
          <Route path="viewtilesquote" element={<ViewTilesQuote />} />
          <Route path="itemsforso" element={<ItemsforQuote />} />
          <Route path="doorsquote" element={<DoorsQuote />} />
          <Route path="viewquotationdetails" element={<ViewQuotationDetails />} />
          <Route path="windowquote" element={<WindowQuote />} />
          <Route path="windowsummary" element={<WindowSummary />} />
          <Route path="quotationsummary" element={<QuotationSummary />} />
          <Route path="doorsummary" element={<DoorSummary />} />
          <Route path="doorquotationsummary" element={<DoorQuotationSummary />} />
          <Route path="tilesordersummary" element={<TilesOrderSummary />} />
          <Route path="MaterialReq" element={<MaterialReq />} />
          <Route path="quotationdetails" element={<QuotationDetails />} />
          <Route path="doorsQuotationDetails" element={<DoorQuotationDetails />} />
          <Route path="windowsquotationdetails" element={<WindowsQuotationDetails />} />
          <Route path="quotesummary" element={<QuoteSummary />} />
          <Route path="doorsquotesummary" element={<DoorsQuoteSummary />} />
          <Route path="windowsquotesummary" element={<WindowsQuotationSummary />} />
          <Route path="summarydetails" element={<SummaryPage />} />
          <Route path="tilesstandardquotedetails" element={<TilesStandardQuoteDetails />} />
          <Route path="tilescustomize" element={<TilesCustomize />} />
          <Route path="windowsstandardquotedetails" element={<WindowsStandardQuoteDetails />} />
          <Route path="windowscustomize" element={<WindowsCustomize />} />
          <Route path="doorsstandardquotedetails" element={<DoorsStandardQuoteDetails />} />
          <Route path="doorscustomize" element={<DoorsCustomize />} />
          <Route path="proceed" element={<Proceed />} />
          <Route path="materiallist" element={<MaterialList />} />
          <Route path="reviewitemlist" element={<ReviewItemList />} />
          <Route path="projectquotes" element={<ProjectQuotes />} />
          <Route path="rfqlist" element={<RFQList />} />
          <Route path="rfqdetails" element={<RFQDetails />} />
          <Route path="orderdetails" element={<CustomerOrderDetails />} />
        </Route>
      </Routes>
    </QueryClientProvider>
  );
}

export default App;